import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle } from "lucide-react";
import { trackLead } from "@/utils/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTY_TYPES = [
  "Einfamilienhaus",
  "Doppelhaushälfte",
  "Reihenhaus",
  "Mehrfamilienhaus",
  "Eigentumswohnung",
  "Grundstück",
  "Gewerbeimmobilie",
  "Sonstiges",
];
const BUDGET_RANGES = [
  "Bis 150.000 €",
  "150.000 – 250.000 €",
  "250.000 – 400.000 €",
  "400.000 – 600.000 €",
  "600.000 – 800.000 €",
  "Über 800.000 €",
];
const DISTRICTS = [
  "Mettmann",
  "Erkrath",
  "Haan",
  "Heiligenhaus",
  "Hilden",
  "Langenfeld",
  "Monheim am Rhein",
  "Ratingen",
  "Velbert",
  "Wülfrath",
  "Anderer / Kreis Mettmann gesamt",
];

export default function LeadForm({
  formType = "contact",
  title,
  ctaText = "Anfrage senden",
  showDistrict = false,
  showBudget = false,
  showPropertyType = true,
  pageSource = "/",
}) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    property_type: "",
    district: "",
    budget: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Bitte geben Sie Ihren Namen und Ihre E-Mail an.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        lead_type: formType,
        page_source: pageSource,
      });
      setSubmitted(true);
      trackLead({ lead_type: formType, page_source: pageSource });
      toast.success("Vielen Dank! Wir melden uns schnellstmöglich.");
    } catch {
      toast.error("Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#0d0d0d] border border-white/10 text-white placeholder-gray-600 px-4 py-3 text-sm focus:outline-none focus:border-[#E61D38] transition-colors";
  const labelCls = "block text-[10px] uppercase tracking-[0.2em] text-gray-400 mb-2";

  if (submitted) {
    return (
      <div
        className="text-center py-12 px-8 bg-[#262626] border border-[#E61D38]/30"
        data-testid="form-success"
      >
        <div className="flex justify-center mb-5">
          <CheckCircle size={48} className="text-[#E61D38]" />
        </div>
        <h3 className="text-white font-bold text-xl uppercase tracking-wide mb-3">
          Anfrage eingegangen!
        </h3>
        <p className="text-gray-400 text-sm leading-relaxed max-w-sm mx-auto">
          Wir haben Ihre Anfrage erhalten und melden uns schnellstmöglich persönlich bei Ihnen.
          <br />
          <br />
          Für dringende Anfragen:{" "}
          <a href="tel:+4921293766829" className="text-[#E61D38] font-bold">
            +49 2129 3766829
          </a>
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[#262626] border border-white/5 p-8 md:p-10" data-testid="lead-form-container">
      {title && (
        <div className="mb-8">
          <div className="h-0.5 w-8 bg-[#E61D38] mb-4" />
          <h3
            className="text-white font-bold text-xl uppercase tracking-wide"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            {title}
          </h3>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5" data-testid="lead-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="name" className={labelCls}>
              Name *
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              value={form.name}
              onChange={handleChange}
              placeholder="Ihr vollständiger Name"
              className={inputCls}
              data-testid="form-name"
            />
          </div>
          <div>
            <label htmlFor="email" className={labelCls}>
              E-Mail *
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              value={form.email}
              onChange={handleChange}
              placeholder="ihre.email@beispiel.de"
              className={inputCls}
              data-testid="form-email"
            />
          </div>
        </div>

        <div>
          <label htmlFor="phone" className={labelCls}>
            Telefon
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            value={form.phone}
            onChange={handleChange}
            placeholder="+49 ..."
            className={inputCls}
            data-testid="form-phone"
          />
        </div>

        {showPropertyType && (
          <div>
            <label htmlFor="property_type" className={labelCls}>
              Immobilientyp
            </label>
            <select
              id="property_type"
              name="property_type"
              value={form.property_type}
              onChange={handleChange}
              className={`${inputCls} cursor-pointer`}
              data-testid="form-property-type"
            >
              <option value="">Bitte wählen...</option>
              {PROPERTY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        )}

        {showDistrict && (
          <div>
            <label htmlFor="district" className={labelCls}>
              Stadt
            </label>
            <select
              id="district"
              name="district"
              value={form.district}
              onChange={handleChange}
              className={`${inputCls} cursor-pointer`}
              data-testid="form-district"
            >
              <option value="">Bitte wählen...</option>
              {DISTRICTS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        )}

        {showBudget && (
          <div>
            <label htmlFor="budget" className={labelCls}>
              Budget
            </label>
            <select
              id="budget"
              name="budget"
              value={form.budget}
              onChange={handleChange}
              className={`${inputCls} cursor-pointer`}
              data-testid="form-budget"
            >
              <option value="">Bitte wählen...</option>
              {BUDGET_RANGES.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label htmlFor="message" className={labelCls}>
            Nachricht
          </label>
          <textarea
            id="message"
            name="message"
            rows={4}
            value={form.message}
            onChange={handleChange}
            placeholder="Beschreiben Sie Ihr Anliegen..."
            className={`${inputCls} resize-none`}
            data-testid="form-message"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 bg-[#E61D38] hover:bg-[#c41930] disabled:bg-gray-700 text-white font-bold py-4 uppercase tracking-wider text-sm transition-colors"
          data-testid="form-submit"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          {loading ? "Wird gesendet..." : ctaText}
        </button>

        <p className="text-gray-600 text-xs text-center">
          Mit dem Absenden stimmen Sie unserer{" "}
          <a href="/datenschutz" className="text-gray-400 underline hover:text-[#E61D38]">
            Datenschutzerklärung
          </a>{" "}
          zu.
        </p>
      </form>
    </div>
  );
}
